import { useEffect, useRef, useState } from 'react'
import useIsomorphicLayoutEffect from './use-isomorphic-layout-effect'

type SpringLoadOptions = {
  delay?: number
  grace?: number
}

/**
 * Drag-hover "spring loading": fires `onTrigger` once after the pointer has
 * hovered a drop target for `delay` ms. Unlike a plain timeout keyed on
 * `isOver`, the countdown survives sub-`grace` ms hover dropouts — react-dnd
 * flickers `isOver` when crossing nested targets, which used to restart the
 * whole countdown.
 *
 * @param active - Pass `isOver && canDrop` from the drop target
 * @param onTrigger - Called once when the delay elapses while still hovered
 * (latest identity is used; changing it does not reset the countdown)
 * @returns Whether the countdown is currently running (drives blink styling)
 */
function useSpringLoad(
  active: boolean,
  onTrigger: (() => void) | undefined,
  { delay = 1000, grace = 100 }: SpringLoadOptions = {},
): boolean {
  const [armed, setArmed] = useState(false)
  const callbackRef = useRef(onTrigger)
  const activeRef = useRef(active)
  const triggerTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const graceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useIsomorphicLayoutEffect(() => {
    callbackRef.current = onTrigger
    activeRef.current = active
  }, [active, onTrigger])

  useEffect(() => {
    if (active) {
      if (graceTimer.current) {
        clearTimeout(graceTimer.current)
        graceTimer.current = null
      }
      if (!triggerTimer.current) {
        setArmed(true)
        triggerTimer.current = setTimeout(() => {
          triggerTimer.current = null
          setArmed(false)
          // A dropout in the last `grace` ms of the countdown leaves its grace
          // timer pending until after this one fires, so the countdown ending
          // is not on its own proof the pointer is still there — only trigger
          // if it came back.
          if (activeRef.current) callbackRef.current?.()
        }, delay)
      }
    } else if (triggerTimer.current && !graceTimer.current) {
      graceTimer.current = setTimeout(() => {
        graceTimer.current = null
        if (triggerTimer.current) {
          clearTimeout(triggerTimer.current)
          triggerTimer.current = null
        }
        setArmed(false)
      }, grace)
    }
  }, [active, delay, grace])

  useEffect(
    () => () => {
      if (triggerTimer.current) clearTimeout(triggerTimer.current)
      if (graceTimer.current) clearTimeout(graceTimer.current)
    },
    [],
  )

  return armed
}

export default useSpringLoad
